import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FaMotorcycle, FaCar, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import useApi from '../../hooks/useApi';

function GuestVehicleForm({ onSuccess }) {
  const { session } = useContext(AuthContext);
  const { roles, communityId } = session || { roles: ['GUEST'], communityId: null };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isUser = roles.includes('USER') && !['SECURITY', 'ADMIN', 'SUPER_ADMIN'].some((r) => roles.includes(r));
  const isPrivileged = ['SECURITY', 'ADMIN', 'SUPER_ADMIN'].some((r) => roles.includes(r));
  const [communities, setCommunities] = useState([]);
  const [formData, setFormData] = useState({
    vehicleNo: '',
    type: '',
    guestName: '',
    visitorCategory: 'GUEST',
    contactNo: '',
    purposeOfVisit: '',
    visitingHouseNo: '',
    division: '',
    entryTime: '',
    communityId: isSuperAdmin ? '' : communityId || '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { post, get } = useApi();

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCommunities();
    }
    if (isUser) {
      fetchUserDetails();
    }
  }, [isUser, isSuperAdmin]);

  const fetchCommunities = async () => {
    try {
      const response = await get('/api/communities/active');
      if (!Array.isArray(response)) {
        throw new Error('Invalid communities response format');
      }
      setCommunities(response);
    } catch (err) {
      console.error('GuestVehicleForm - Fetch communities error:', err);
      setError('Failed to load communities.');
      toast.error('Failed to load communities.');
    }
  };

  const fetchUserDetails = async () => {
    try {
      const response = await get('/api/users/current');
      setFormData((prev) => ({
        ...prev,
        contactNo: response.mobileNo || '',
        visitingHouseNo: response.houseNo || '',
        division: response.division || '',
        communityId: response.communityId || communityId || '',
      }));
    } catch (err) {
      console.error('GuestVehicleForm - Fetch user error:', err);
      setError('Failed to load user details.');
      toast.error('Failed to load user details.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((isUser || isPrivileged) && !formData.communityId) {
      setError('Please select a community.');
      toast.error('Please select a community.');
      return;
    }
    try {
      setIsSubmitting(true);
      setError('');
      const payload = {
        ...formData,
        vehicleNo: formData.vehicleNo.replace(/[\s-]/g, '').toUpperCase(),
      };
      await post('/api/guest-vehicles', payload);
      toast.success('Visitor created with planned status.');
      setFormData({
        vehicleNo: '',
        type: '',
        guestName: '',
        visitorCategory: 'GUEST',
        contactNo: isUser ? formData.contactNo : '',
        purposeOfVisit: '',
        visitingHouseNo: isUser ? formData.visitingHouseNo : '',
        division: isUser ? formData.division : '',
        entryTime: '',
        communityId: isSuperAdmin ? '' : communityId || '',
      });
      setError('');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="guest-form-panel">
      <div className="card-body">
        <span className="vehicle-panel-kicker">Visitor planning</span>
        <h5 className="vehicle-panel-title">Create visitor entry</h5>
        <p className="vehicle-panel-copy">Add the expected visit now; the creator or gate team can mark arrival and departure later.</p>
        {error && <Alert type="danger" message={error} />}
        <form onSubmit={handleSubmit}>
          <div className="mb-3"><label htmlFor="visitorCategory" className="form-label">Visitor category</label><select id="visitorCategory" name="visitorCategory" className="form-select" value={formData.visitorCategory} onChange={handleChange}>{['GUEST','DELIVERY','SERVICE','CAB','VENDOR','OTHER'].map(category => <option key={category} value={category}>{category.charAt(0)+category.slice(1).toLowerCase()}</option>)}</select></div>
          {isSuperAdmin && (
            <div className="mb-3">
              <label htmlFor="communityId" className="form-label">
                Select Community
              </label>
              <select
                id="communityId"
                className="form-select"
                name="communityId"
                value={formData.communityId}
                onChange={handleChange}
                disabled={communities.length === 0}
                required
              >
                <option value="">Select a Community</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <FormInput
            label="Vehicle Number"
            name="vehicleNo"
            value={formData.vehicleNo}
            onChange={(event) => handleChange({
              target: { name: event.target.name, value: event.target.value.toUpperCase() },
            })}
            required
          />
          <div className="mb-3">
            <label className="form-label">Vehicle Type</label>
            <div className="d-flex justify-content-around">
              <div className="form-check vehicle-type-radio">
                <input
                  type="radio"
                  className="form-check-input"
                  id="type-two-wheeler"
                  name="type"
                  value="two-wheeler"
                  checked={formData.type === 'two-wheeler'}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label d-flex align-items-center" htmlFor="type-two-wheeler">
                  <FaMotorcycle size={30} color="#007bff" className="me-2" />
                  Two-Wheeler
                </label>
              </div>
              <div className="form-check vehicle-type-radio">
                <input
                  type="radio"
                  className="form-check-input"
                  id="type-four-wheeler"
                  name="type"
                  value="four-wheeler"
                  checked={formData.type === 'four-wheeler'}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label d-flex align-items-center" htmlFor="type-four-wheeler">
                  <FaCar size={30} color="#28a745" className="me-2" />
                  Four-Wheeler
                </label>
              </div>
            </div>
          </div>
          <FormInput
            label="Visitor Name"
            name="guestName"
            value={formData.guestName}
            onChange={handleChange}
            required
          />
          <div className="mb-3">
            <label htmlFor="contactNo" className="form-label">Contact Number</label>
            <input
              type="tel"
              className="form-control"
              id="contactNo"
              name="contactNo"
              value={formData.contactNo}
              onChange={handleChange}
              pattern="[0-9]{10}"
              required
              disabled={isUser && !isPrivileged}
            />
          </div>
          <FormInput
            label="Purpose of Visit"
            name="purposeOfVisit"
            value={formData.purposeOfVisit}
            onChange={handleChange}
            required
          />
          <div className="mb-3">
            <label htmlFor="visitingHouseNo" className="form-label">Visiting House Number</label>
            <input
              type="text"
              className="form-control"
              id="visitingHouseNo"
              name="visitingHouseNo"
              value={formData.visitingHouseNo}
              onChange={handleChange}
              required
              disabled={isUser && !isPrivileged}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="division" className="form-label">Division</label>
            {isUser && !isPrivileged ? (
              <input
                type="text"
                className="form-control"
                name="division"
                value={formData.division}
                disabled
              />
            ) : (
              <select
                className="form-select"
                name="division"
                value={formData.division}
                onChange={handleChange}
                required
              >
                <option value="">Select Division</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </select>
            )}
          </div>
          <FormInput
            label="Entry Time"
            name="entryTime"
            type="datetime-local"
            value={formData.entryTime}
            onChange={handleChange}
            required
          />
          <Button type="submit" variant="primary" className="w-100" disabled={isSubmitting}>
            <FaPlus /> {isSubmitting ? 'Creating visit…' : 'Plan visitor'}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default GuestVehicleForm;

GuestVehicleForm.propTypes = {
  onSuccess: PropTypes.func,
};
